import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { messagingApi } from '../../support/supportApi';
import StartConversationButton from './StartConversationButton';
import { normalizeMessagingUser } from './messagingUserUtils';
import { applyProfileAvatarImgFallback, PROFILE_UPDATED_EVENT } from '../profilePictureUrl';
import { dispatchMessagingUnreadChanged } from './messagingEvents';
import { SHELL_HEADER_DROPDOWN_Z_INDEX } from './messagingUtils';
import './Messaging.css';

const DROPDOWN_WIDTH = 400;

function truncateMessage(msg, maxLength) {
  const text = String(msg || '');
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
}

function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * Header dropdown preview of recent conversations.
 */
export default function MessagingDropdown({
  portaled = true,
  anchorRef = null,
  onNavigateToAll,
  onSelectConversation,
}) {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const dropdownRef = useRef(null);

  const goNewMessage = () => {
    if (onNavigateToAll) onNavigateToAll();
    navigate('/sm/all-messages?compose=1');
  };

  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await messagingApi.conversations({ limit: 5 });
      if (data?.success) setConversations(data.conversations || []);
    } catch {
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    const onProfileUpdated = () => loadConversations();
    window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
  }, [loadConversations]);

  const updateDropdownPosition = useCallback(() => {
    if (!portaled) {
      setDropdownStyle(null);
      return;
    }
    const anchor = anchorRef?.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const gap = 8;
    const maxHeight = Math.max(160, window.innerHeight - rect.bottom - gap - 16);
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + gap,
      right: Math.max(8, window.innerWidth - rect.right),
      width: DROPDOWN_WIDTH,
      maxHeight,
      zIndex: SHELL_HEADER_DROPDOWN_Z_INDEX,
    });
  }, [anchorRef, portaled]);

  useLayoutEffect(() => {
    if (!portaled) return undefined;
    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);
    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [portaled, updateDropdownPosition, loading, conversations.length]);

  const openAll = () => {
    if (onNavigateToAll) onNavigateToAll();
    navigate('/sm/all-messages');
  };

  const openConversation = async (conv) => {
    const userId = conv?.otherUser?.userId;
    const conversationId = conv?.conversationId;
    if (conversationId && (conv.unreadCount || 0) > 0) {
      try {
        await messagingApi.markRead({ conversationId });
        dispatchMessagingUnreadChanged();
        setConversations((prev) =>
          prev.map((c) => (c.conversationId === conversationId ? { ...c, unreadCount: 0 } : c))
        );
      } catch {
        /* ignore */
      }
    }
    if (onSelectConversation) {
      onSelectConversation(conv);
      return;
    }
    if (userId) navigate(`/sm/all-messages?userId=${encodeURIComponent(userId)}`);
    else openAll();
  };

  const dropdownPanel = (
    <div
      ref={dropdownRef}
      className={`messaging-dropdown${portaled ? ' messaging-dropdown--portal' : ''}`}
      style={portaled ? dropdownStyle || undefined : undefined}
    >
      <div className="messaging-heading-row">
        <h3 className="messaging-heading">Messages</h3>
        <StartConversationButton
          showLabel={false}
          className="messaging-dropdown__compose-btn"
          onClick={goNewMessage}
        />
      </div>
      {loading ? (
        <ul className="messaging-skeleton-list" aria-busy="true" aria-label="Loading messages">
          {[1, 2, 3].map((i) => (
            <li key={i} className="messaging-dropdown__skeleton-item">
              <span className="messaging-skeleton-circle" />
              <span className="messaging-skeleton-body">
                <span className="messaging-skeleton-line messaging-skeleton-line-long" />
                <span className="messaging-skeleton-line messaging-skeleton-line-short" />
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {!loading && !conversations.length ? (
        <p className="messaging-dropdown__status">No messages yet.</p>
      ) : null}
      {!loading ? (
      <ul>
        {conversations.map((conv) => {
          const other = normalizeMessagingUser(conv.otherUser);
          return (
          <li key={conv.conversationId}>
            <button
              type="button"
              className={`messaging-dropdown__item noOutline-text${
                (conv.unreadCount || 0) > 0 ? ' has-unread' : ''
              }`}
              onClick={() => openConversation(conv)}
            >
              <img
                src={other?.profilePictureUrl}
                alt=""
                className="messaging-dropdown__avatar"
                onError={(e) => {
                  applyProfileAvatarImgFallback(e.currentTarget, {
                    userId: other?.userId,
                    name: other?.displayName,
                  });
                }}
              />
              <span className="msg-labels">
                <span className="messaging-dropdown__name">{other?.displayName || 'User'}</span>
                <span className="messaging-dropdown__preview">
                  {truncateMessage(conv.lastMessagePreview || 'No messages yet', 40)}
                </span>
              </span>
              <span className="timestamp">{formatTime(conv.lastMessageAt)}</span>
              {conv.unreadCount > 0 ? (
                <span className="messaging-dropdown__unread">{conv.unreadCount}</span>
              ) : null}
            </button>
          </li>
          );
        })}
      </ul>
      ) : null}

      <div className="all-messages" onClick={openAll} role="button" tabIndex={0}>
        See all messages
      </div>
    </div>
  );

  if (portaled) {
    return dropdownStyle ? createPortal(dropdownPanel, document.body) : null;
  }

  return <div className="messaging-container messaging-container--inline">{dropdownPanel}</div>;
}
